import type { Breed } from 'shared/@generated/models';

// Cost estimates in SEK. The dog defaults follow SKK's 2026 example for a golden retriever
// (about 49 000 kr the first year, 21 000 kr the following years); cat defaults follow
// SVERAK's and Trygg-Hansa's cost tables. Other markets are scaled by MARKET_PRICE_FACTOR.

export type PetCostAnimal = 'dog' | 'cat';
export type PetCostStage = 'young' | 'adult' | 'senior';
export type PetCostCategory =
  | 'purchase'
  | 'food'
  | 'insurance'
  | 'vet'
  | 'equipment'
  | 'grooming'
  | 'litter'
  | 'care'
  | 'training';

export type FoodLevel = 'budget' | 'standard' | 'premium';
export type InsuranceLevel = 'basic' | 'standard' | 'extended';
export type GroomingLevel = 'self' | 'salon';
export type CareLevel =
  | 'none'
  | 'sometimes'
  | 'part_time'
  | 'daycare_1'
  | 'daycare_2'
  | 'daycare_3'
  | 'daycare_5';

export interface PetCostProfile {
  weightKg: number;
  lifespanYears: number;
  groomingNeeds: number;
}

export interface PetCostSettings {
  purchasePrice: number;
  food: FoodLevel;
  insurance: InsuranceLevel;
  grooming: GroomingLevel;
  care: CareLevel;
  training: boolean;
}

export type PetCostBreakdown = Partial<Record<PetCostCategory, number>>;

export interface PetCostStageSummary {
  stage: PetCostStage;
  firstYear: number;
  lastYear: number;
  perYear: PetCostBreakdown;
  perYearTotal: number;
}

export const petCostStages: PetCostStage[] = ['young', 'adult', 'senior'];
export const foodLevels: FoodLevel[] = ['budget', 'standard', 'premium'];
export const insuranceLevels: InsuranceLevel[] = ['basic', 'standard', 'extended'];
export const groomingLevels: GroomingLevel[] = ['self', 'salon'];
export const careLevels: Record<PetCostAnimal, CareLevel[]> = {
  dog: ['none', 'sometimes', 'daycare_1', 'daycare_2', 'daycare_3', 'daycare_5'],
  cat: ['none', 'sometimes', 'part_time'],
};

export const petCostCategories: Record<PetCostAnimal, PetCostCategory[]> = {
  dog: ['purchase', 'food', 'insurance', 'vet', 'equipment', 'grooming', 'care', 'training'],
  cat: ['purchase', 'food', 'litter', 'insurance', 'vet', 'equipment', 'care'],
};

// Established site colors (shared/library/colors.ts).
export const petCostColors: Record<PetCostCategory, string> = {
  purchase: '#f97316',
  food: '#fcd34d',
  insurance: '#7cc4f0',
  vet: '#cfe5ce',
  equipment: '#c4b5fd',
  grooming: '#f9a8c9',
  litter: '#eedecd',
  care: '#fdba74',
  training: '#d6d3d1',
};

const MARKET_PRICE_FACTOR: Record<string, number> = { SE: 1, NO: 1.05, DK: 0.7, FI: 0.09 };

const defaultProfiles: Record<PetCostAnimal, PetCostProfile> = {
  dog: { weightKg: 20, lifespanYears: 12, groomingNeeds: 3 },
  cat: { weightKg: 4.5, lifespanYears: 15, groomingNeeds: 2 },
};

const defaultPurchasePrice: Record<PetCostAnimal, number> = { dog: 25000, cat: 10000 };

const foodFactor: Record<FoodLevel, number> = { budget: 0.65, standard: 1, premium: 1.5 };
const insuranceFactor: Record<InsuranceLevel, number> = { basic: 0.7, standard: 1, extended: 1.3 };
const careCost: Record<PetCostAnimal, Partial<Record<CareLevel, number>>> = {
  dog: {
    none: 0,
    sometimes: 6000,
    daycare_1: 17000,
    daycare_2: 30000,
    daycare_3: 41000,
    daycare_5: 55000,
  },
  cat: { none: 0, sometimes: 2500, part_time: 6000 },
};

export function marketPriceFactor(countryCode: string) {
  return MARKET_PRICE_FACTOR[countryCode] ?? 1;
}

export function defaultPetCostProfile(animal: PetCostAnimal): PetCostProfile {
  return defaultProfiles[animal];
}

export function petCostProfileFromBreed(animal: PetCostAnimal, breed: Breed): PetCostProfile {
  const fallback = defaultProfiles[animal];
  const weights = [
    breed.weight_male_min,
    breed.weight_male_max,
    breed.weight_female_min,
    breed.weight_female_max,
  ].filter((weight) => weight > 0);
  const lifespans = [breed.life_expectancy_min, breed.life_expectancy_max].filter((age) => age > 0);

  return {
    weightKg: weights.length
      ? weights.reduce((sum, weight) => sum + weight, 0) / weights.length
      : fallback.weightKg,
    lifespanYears: lifespans.length
      ? clamp(Math.round(lifespans.reduce((sum, age) => sum + age, 0) / lifespans.length), 7, 20)
      : fallback.lifespanYears,
    groomingNeeds: breed.grooming_needs || fallback.groomingNeeds,
  };
}

export function defaultPetCostSettings(
  animal: PetCostAnimal,
  countryCode: string,
  marketPrice?: number | null,
): PetCostSettings {
  return {
    purchasePrice:
      marketPrice ?? roundTo(defaultPurchasePrice[animal] * marketPriceFactor(countryCode), 1000),
    food: 'standard',
    insurance: 'standard',
    grooming: 'self',
    care: 'none',
    training: animal === 'dog',
  };
}

// Premiums rise with age: a discounted first year, then about 5 % per year.
function insuranceAgeFactor(year: number) {
  return year === 1 ? 0.82 : 1.1 + 0.05 * (year - 2);
}

export function petCostForYear(
  animal: PetCostAnimal,
  year: number,
  profile: PetCostProfile,
  settings: PetCostSettings,
  countryCode: string,
): PetCostBreakdown {
  const senior = stageForYear(year, profile.lifespanYears) === 'senior';
  const factor = marketPriceFactor(countryCode);
  const care = careCost[animal][settings.care] ?? 0;
  let costs: PetCostBreakdown;

  if (animal === 'dog') {
    const size = profile.weightKg / 30;
    const salonVisits = profile.groomingNeeds >= 4 ? 6 : 4;
    const grooming =
      settings.grooming === 'salon' ? salonVisits * (700 + 15 * profile.weightKg) : 400;

    costs = {
      food:
        9000 * size ** 0.75 * foodFactor[settings.food] * (year === 1 ? 1.1 : senior ? 0.95 : 1),
      insurance:
        6200 * size ** 0.4 * insuranceFactor[settings.insurance] * insuranceAgeFactor(year),
      vet: (year === 1 ? 2500 : senior ? 4500 : 1800) * size ** 0.2,
      equipment: year === 1 ? 3500 : 2000,
      grooming: year === 1 ? grooming / 2 : grooming,
      care,
      training: year === 1 && settings.training ? 2100 : 0,
    };
  } else {
    costs = {
      food: 2500 * (profile.weightKg / 4.5) ** 0.75 * foodFactor[settings.food],
      litter: 1300,
      insurance: 2400 * insuranceFactor[settings.insurance] * insuranceAgeFactor(year),
      vet: year === 1 ? 3300 : senior ? 2500 : 700,
      equipment: year === 1 ? 2500 : 600,
      care,
    };
  }

  const rounded: PetCostBreakdown = {};

  for (const [category, amount] of Object.entries(costs) as [PetCostCategory, number][]) {
    if (amount > 0) rounded[category] = roundTo(amount * factor, 100);
  }

  if (year === 1 && settings.purchasePrice > 0) {
    rounded.purchase = roundTo(settings.purchasePrice, 100);
  }

  return rounded;
}

// Lifespans are clamped to 7–20 years (petCostProfileFromBreed), so every stage has at least one year.
export function seniorStartYear(lifespanYears: number) {
  return lifespanYears - Math.max(2, Math.round(lifespanYears * 0.3)) + 1;
}

export function stageForYear(year: number, lifespanYears: number): PetCostStage {
  if (year === 1) return 'young';

  return year >= seniorStartYear(lifespanYears) ? 'senior' : 'adult';
}

export function sumCosts(breakdown: PetCostBreakdown) {
  return Object.values(breakdown).reduce((sum, amount) => sum + amount, 0);
}

export function petCostYears(
  animal: PetCostAnimal,
  profile: PetCostProfile,
  settings: PetCostSettings,
  countryCode: string,
) {
  return Array.from({ length: profile.lifespanYears }, (_, index) =>
    petCostForYear(animal, index + 1, profile, settings, countryCode),
  );
}

export function summarizePetCostStages(
  years: PetCostBreakdown[],
  lifespanYears: number,
): PetCostStageSummary[] {
  const seniorStart = seniorStartYear(lifespanYears);
  const ranges: Record<PetCostStage, [number, number]> = {
    young: [1, 1],
    adult: [2, seniorStart - 1],
    senior: [seniorStart, lifespanYears],
  };

  return petCostStages.map((stage) => {
    const [firstYear, lastYear] = ranges[stage];
    const stageYears = years.slice(firstYear - 1, lastYear);
    const totals: PetCostBreakdown = {};

    for (const year of stageYears) {
      for (const [category, amount] of Object.entries(year) as [PetCostCategory, number][]) {
        totals[category] = (totals[category] ?? 0) + amount;
      }
    }

    const perYear: PetCostBreakdown = {};

    for (const [category, amount] of Object.entries(totals) as [PetCostCategory, number][]) {
      perYear[category] = roundTo(amount / stageYears.length, 100);
    }

    return {
      stage,
      firstYear,
      lastYear,
      perYear,
      perYearTotal: sumCosts(perYear),
    };
  });
}

function roundTo(value: number, step: number) {
  return Math.round(value / step) * step;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
